import {
    EpsonAlign,
    EpsonBarcode,
    EpsonCut,
    EpsonDrawer,
    EpsonFont,
    EpsonHRI,
    EpsonLevel,
    EpsonPulse,
    EpsonSymbol,
    NFSendResult,
    NFThermalPrinter,
} from 'src/app/shared/model/nf-thermal-printer.model';

import {
    NFThermalDriverBase,
    NFThermalPrinterConfig
} from './nf-thermal-driver-base';

import { TilbyTcpSocketUtils } from 'src/app/shared/net/tilby-tcp-socket';
import { EscPosEncoder } from './escpos-encoder';
import { chunk } from 'src/app/shared/utils';

interface NetworkSettings {
    ipAddress: string;
    port: number;
    connection?: 'sp' | 'ts';
}

const defaultLineSpc = 0x50; //Default line spacing

type EscPosBitmapData = {
    width: number;
    height: number;
    data: Uint8Array;
}

/**
 * @class EscPosDriver
 * @implements {NFThermalPrinter}
 * Implements the NFThermalPrinter interface using ESC/POS commands.
 */
export class EscPosDriver extends NFThermalDriverBase implements NFThermalPrinter {
    escPosDoc: number[];

    /**
     * Constructs a new EscPosDriver instance.
     * @param {string} ipAddress - IP address of the printer.
     * @param {number} [port=9100] - Port number of the printer.
     * @param {NFThermalPrinterConfig} [configuration] - Optional configuration object with connection type and codepage mapping.
     */
    constructor(ipAddress: string, port: number = 9100, configuration?: NFThermalPrinterConfig) {
        super(ipAddress, port, configuration);

        this.escPosDoc = [
            0x1b, 0x3d, 0x01, //Set peripheral device
            0x1b, 0x40, //Reset printer
            0x1b, 0x33, (this.printSettings.linespc ?? defaultLineSpc) //Set line spacing
        ];
    }

    /**
     * Establishes a connection with the printer and sends the document.
     * @param {NetworkSettings} target - Network settings of the printer.
     * @param {number[]} docArray - Array of ESC/POS commands to send.
     * @returns {Promise<void>} A promise that resolves when the document is sent successfully.
     * @throws {string} 'UNSUPPORTED_PLATFORM' if the platform doesn't support serial ports or TCP sockets.
     * @throws {string} 'CONNECTION_ERROR' if there is a problem with the connection.
     */
    private static async connectAndSend(target: NetworkSettings, docArray: number[]): Promise<void> {
        if (target.ipAddress === '0.0.0.0') {
            return;
        }

        switch (target.connection) {
            case 'sp': //Serial port
                if (!window.require) {
                    throw 'UNSUPPORTED_PLATFORM';
                }

                const { SerialPort } = window.require('serialport');
                const targetPort = new SerialPort({ path: target.ipAddress, baudRate: 9600, autoOpen: false });

                return new Promise((resolve, reject) => {
                    targetPort.open((err: any) => {
                        if (err) {
                            return reject('CONNECTION_ERROR');
                        }

                        targetPort.write(docArray, (err: any) => {
                            if (err) {
                                reject('CONNECTION_ERROR');
                            } else {
                                resolve();
                            }

                            targetPort.close();
                        });
                    });
                });
                break;
            case 'ts': //TCP Socket (Default)
            default:
                const tcpSocket = TilbyTcpSocketUtils.getSocket();

                if (!tcpSocket) {
                    throw 'UNSUPPORTED_PLATFORM';
                }

                try {
                    await tcpSocket.connect(target.ipAddress, target.port, { timeout: 3000 });
                    await tcpSocket.send(Uint8Array.from(docArray));
                } catch (error) {
                    throw 'CONNECTION_ERROR';
                } finally {
                    await new Promise((resolve) => {
                        tcpSocket.close().finally(() => resolve(null));
                        //Regardless of socket disconnection, return to caller after 5 seconds
                        setTimeout(resolve, 5000);
                    });
                }
                break;
        }
    };

    /**
     * Encodes text to ESC/POS commands based on the provided codepage mapping.
     * @param {string} text - Text to be encoded.
     * @param {string | undefined} codepageMapping - Codepage mapping to use for encoding.
     * @returns {number[]} Array of ESC/POS commands representing the encoded text.
     */
    private encodeText(text: string, codepageMapping: string | undefined): number[] {
        const result: number[] = [];
        const encodedData = EscPosEncoder.encode(text, codepageMapping);

        for (const set of encodedData) {
            result.push(0x1b, 0x74, set.codepageCommand, ...set.bytes);
        }

        return result;
    };

    /**
     * Generates ESC/POS command for setting line spacing.
     * @param {number} linespc - Line spacing value.
     * @returns {number[]} Array of ESC/POS commands.
     */
    private getLineSpace(linespc: number): number[] {
        return [0x1b, 0x33, linespc];
    };

    /**
     * Generates ESC/POS command for setting print mode (double width, double height, emphasis, underline).
     * @param {boolean} dw - Double width flag.
     * @param {boolean} dh - Double height flag.
     * @param {boolean} em - Emphasis flag.
     * @param {boolean} ul - Underline flag.
     * @returns {number[]} Array of ESC/POS commands.
     */
    private setPrintMode(dw: boolean, dh: boolean, em: boolean, ul: boolean): number[] {
        return [0x1b, 0x21, ((ul ? 128 : 0) + (dw ? 32 : 0) + (dh ? 16 : 0) + (em ? 8 : 0))];
    };

    /**
    * Generates ESC/POS command for setting reverse printing mode.
    * @param {boolean} reverse - Reverse print flag.
    * @returns {number[]} Array of ESC/POS commands.
    */
    private getReverse(reverse: boolean): number[] {
        return [0x1d, 0x42, (reverse ? 1 : 0)];
    };

    /**
     * Generates ESC/POS command for setting character size (width and height).
     * @param {number} width - Width of the characters.
     * @param {number} height - Height of the characters.
     * @returns {number[]} Array of ESC/POS commands.
     */
    private getSizeCommands(width: number, height: number): number[] {
        return [0x1d, 0x21, ((height - 1) * 16) + (width - 1)];
    };

    /**
     * Generates ESC/POS command for setting text alignment.
     * @param {EpsonAlign} align - Text alignment option.
     * @returns {number[]} Array of ESC/POS commands.
     */
    private getAlignment(align: EpsonAlign): number[] {
        switch (align) {
            case EpsonAlign.CENTER:
                return [0x1b, 0x61, 0x31];
            case EpsonAlign.LEFT:
                return [0x1b, 0x61, 0x30];
            case EpsonAlign.RIGHT:
                return [0x1b, 0x61, 0x32];
            default:
                return [0x1b, 0x61, 0x30];
        }
    };

    /**
     * Adds text to the document.
     * @param {string} text - Text to be added.
     */
    public addText(text: string): void {
        if (this.driverConfig.eReceiptMode) {
            return;
        }

        if (this.printSettings.dw !== this.lastPrintSettings.dw || this.printSettings.dh !== this.lastPrintSettings.dh || this.printSettings.em !== this.lastPrintSettings.em || this.printSettings.ul !== this.lastPrintSettings.ul) {
            this.escPosDoc.push(...this.setPrintMode(this.printSettings.dw, this.printSettings.dh, this.printSettings.em, this.printSettings.ul));
        }
        if (this.printSettings.width !== this.lastPrintSettings.width || this.printSettings.height !== this.lastPrintSettings.height) {
            this.escPosDoc.push(...this.getSizeCommands(this.printSettings.width, this.printSettings.height));
        }
        if (this.printSettings.align !== this.lastPrintSettings.align) {
            this.escPosDoc.push(...this.getAlignment(this.printSettings.align));
        }
        if (this.printSettings.linespc !== this.lastPrintSettings.linespc) {
            this.escPosDoc.push(...this.getLineSpace(this.printSettings.linespc ?? defaultLineSpc));
        }
        if (this.printSettings.reverse !== this.lastPrintSettings.reverse) {
            this.escPosDoc.push(...this.getReverse(this.printSettings.reverse));
        }

        this.escPosDoc.push(...this.encodeText(text, this.driverConfig.codepageMapping));
        this.lastPrintSettings = { ...this.printSettings };
    };

    /**
     * Adds a logo to the document.
     * @param {number} key1 - First key for logo selection (0-255).
     * @param {number} key2 - Second key for logo selection (0-255).
     */
    public addLogo(key1: number, key2: number): void {
        if (this.driverConfig.eReceiptMode) {
            return;
        }

        this.escPosDoc.push(0x1d, 0x28, 0x4c, 0x06, 0x00, 0x30, 0x45, ((key1 >= 0 && key1 <= 255) ? key1 : 0x20), ((key2 >= 0 && key2 <= 255) ? key2 : 0x20), 0x01, 0x01);
    };

    /**
     * Gets the barcode type code.
     * @param {string} type - Barcode type.
     * @returns {number} Barcode type code.
     */
    private getBarcodeTypeCode(type: string): number {
        switch (type) {
            case EpsonBarcode.UPC_A:
                return 0x41;
            case EpsonBarcode.UPC_E:
                return 0x42;
            case EpsonBarcode.EAN13:
            case EpsonBarcode.JAN13:
                return 0x43;
            case EpsonBarcode.EAN8:
            case EpsonBarcode.JAN8:
                return 0x44;
            case EpsonBarcode.CODE39:
                return 0x45;
            case EpsonBarcode.ITF:
                return 0x46;
            case EpsonBarcode.CODABAR:
                return 0x47;
            case EpsonBarcode.CODE93:
                return 0x48;
            case EpsonBarcode.CODE128:
                return 0x49;
            case EpsonBarcode.GS1_128:
                return 0x4a;
            case EpsonBarcode.GS1_DATABAR_OMNIDIRECTIONAL:
                return 0x4b;
            case EpsonBarcode.GS1_DATABAR_TRUNCATED:
                return 0x4c;
            case EpsonBarcode.GS1_DATABAR_LIMITED:
                return 0x4d;
            case EpsonBarcode.GS1_DATABAR_EXPANDED:
                return 0x4e;
            default:
                return 0x45; //CODE39
        }
    };

    /**
     * Gets the HRI (Human Readable Interpretation) code.
     * @param {string} hri - HRI position.
     * @returns {number} HRI code.
     */
    private getHRICode(hri: string): number {
        switch (hri) {
            case EpsonHRI.NONE:
                return 0x00;
            case EpsonHRI.ABOVE:
                return 0x01;
            case EpsonHRI.BELOW:
                return 0x02;
            case EpsonHRI.BOTH:
                return 0x03;
            default:
                return 0x00;
        }
    };

    /**
     * Gets the font code.
     * @param {string} font - Font type.
     * @returns {number} Font code.
     */
    private getFontCode(font: string): number {
        switch (font) {
            case EpsonFont.FONT_A:
                return 0x00;
            case EpsonFont.FONT_B:
                return 0x01;
            case EpsonFont.FONT_C:
                return 0x02;
            case EpsonFont.FONT_SPECIAL_A:
                return 0x61;
            case EpsonFont.FONT_SPECIAL_B:
                return 0x62;
            default:
                return 0x00;
        }
    };

    /**
    * Gets the QR code correction level code.
    * @param {string} level - Error correction level.
    * @returns {number} QR code correction level code.
    */
    private getQrCodeCorrectionLevel(level: string): number {
        switch (level) {
            case EpsonLevel.LEVEL_L:
                return 0x30;
            case EpsonLevel.LEVEL_M:
                return 0x31;
            case EpsonLevel.LEVEL_Q:
                return 0x32;
            case EpsonLevel.LEVEL_H:
                return 0x33;
            case EpsonLevel.LEVEL_DEFAULT:
                return 0x30;
            default:
                return 0x30;
        }
    };

    /**
     * Adds a barcode to the document.
     * @param {string} data - Barcode data.
     * @param {EpsonBarcode} type - Barcode type.
     * @param {EpsonHRI} [hri] - HRI position.
     * @param {EpsonFont} [font] - Font for HRI.
     * @param {number} [width] - Width of barcode.
     * @param {number} [height] - Height of barcode.
     */
    public addBarcode(data: string, type: EpsonBarcode, hri?: EpsonHRI, font?: EpsonFont, width?: number, height?: number): void {
        if (this.driverConfig.eReceiptMode) {
            return;
        }

        if (hri) {
            this.escPosDoc.push(...[0x1d, 0x48, this.getHRICode(hri)]);
        }

        if (font) {
            this.escPosDoc.push(...[0x1d, 0x66, this.getFontCode(font)]);
        }

        if (width && width > 0 && width < 256) {
            this.escPosDoc.push(...[0x1d, 0x77, width]);
        }

        if (height && height > 0 && height < 256) {
            this.escPosDoc.push(...[0x1d, 0x68, height]);
        }

        this.escPosDoc.push(...[0x1d, 0x6b, this.getBarcodeTypeCode(type), data.length, ...data.split('').map((l) => l.charCodeAt(0))]);
    };

    /**
     * Adds a symbol (like QR code) to the document.
     * @param {string} data - Symbol data.
     * @param {EpsonSymbol} type - Symbol type.
     * @param {EpsonLevel} [level] - Error correction level.
     * @param {number} [width] - Width of symbol.
     * @param {number} [height] - Height of symbol (not used in QR codes).
     * @param {number} [size] - Size of symbol (not used in QR codes).
     */
    public addSymbol(data: string, type: EpsonSymbol, level?: EpsonLevel, width?: number, height?: number, size?: number): void {
        if (this.driverConfig.eReceiptMode) {
            return;
        }

        const symbolCommands = [0x1d, 0x28, 0x6b];
        const dataSize = data.length;

        let typeCode: number = 0;
        let functionParams: { [key: number]: number[] } = {};

        switch (type) {
            case EpsonSymbol.QRCODE_MODEL_1:
                typeCode = 0x31;
                functionParams = { 0x41: [0x31, 0x00] };
                break;
            case EpsonSymbol.QRCODE_MODEL_2:
                typeCode = 0x31;
                functionParams = { 0x41: [0x32, 0x00] };
                break;
            case EpsonSymbol.QRCODE_MICRO:
                typeCode = 0x31;
                functionParams = { 0x41: [0x33, 0x00] };
                break;
        }

        switch (typeCode) {
            case 0x31: //QR Code
                this.escPosDoc.push(...symbolCommands, 0x04, 0x00, typeCode, 0x41, ...functionParams[0x41]); //Select QR Code model
                this.escPosDoc.push(...symbolCommands, 0x03, 0x00, typeCode, 0x43, width || 0); //Set size (use width parameter for matching the ePos API)
                this.escPosDoc.push(...symbolCommands, 0x03, 0x00, typeCode, 0x45, this.getQrCodeCorrectionLevel(level || 'default')); //Set correction level
                this.escPosDoc.push(...symbolCommands, (dataSize % 256) + 3, Math.floor(dataSize / 256), typeCode, 0x50, 0x30, ...(data.split('').map((l) => l.charCodeAt(0)))); //Store the QR Code data
                this.escPosDoc.push(...symbolCommands, 0x03, 0x00, typeCode, 0x51, 0x30); //Print the QR Code
                break;
        }
    };

    /**
     * Adds a cut command to the document.
     * @param {EpsonCut} [type] - Cut type.
     */
    public addCut(type?: EpsonCut): void {
        if (this.driverConfig.eReceiptMode) {
            return;
        }

        let typeCode: number;

        switch (type) {
            case EpsonCut.FEED:
                typeCode = 0x42;
                break;
            case EpsonCut.NO_FEED:
                typeCode = 0x31;
                break;
            case EpsonCut.RESERVE:
                typeCode = 0x62;
                break;
            default:
                typeCode = 0x42;
        }

        this.escPosDoc.push(0x0a, 0x0d, 0x0a, 0x0d, 0x1d, 0x56, typeCode);

        if (typeCode !== 49) { //FEED and RESERVE require an additional parameter
            this.escPosDoc.push(0x00);
        }
    };

    /**
     * Processes an image for ESC/POS printing.
     * This version assumes the input image is already black and white or grayscale
     * and performs a simple thresholding conversion to a 1-bit bitmap.
     *
     * @param {string} imageUrl - Data URL of the JPEG image (base64 encoded).
     * @param {number} targetWidth - Desired width of the image in dots for the printer.
     * @returns {Promise<void>} A promise that resolves when the image has been added to the document
     */
    public async addImage(imageUrl: string, targetWidth = 576): Promise<void> {
        return new Promise((resolve, reject) => {
            const img = new Image();

            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d', { willReadFrequently: true });

                if (!ctx) {
                    reject();
                    return;
                }

                // Calculate proportional height
                const aspectRatio = img.width / img.height;
                const scaleY = 2;
                const originalHeight = Math.round(targetWidth / aspectRatio);
                const targetHeight = originalHeight * scaleY;

                canvas.width = targetWidth;
                canvas.height = targetHeight;

                // Draw the resized image onto the canvas
                ctx.scale(1, scaleY);
                ctx.drawImage(img, 0, 0, targetWidth, originalHeight);

                // Get pixel data
                const imageData = ctx.getImageData(0, 0, targetWidth, targetHeight);
                const data = imageData.data; // RGBA array [R, G, B, A, R, G, B, A, ...]

                // Initialize a 1-bit bitmap array (1 for black, 0 for white)
                const bitmap = new Uint8Array(targetWidth * targetHeight);

                // Convert to 1-bit black and white using a simple threshold
                for (let y = 0; y < targetHeight; y++) {
                    for (let x = 0; x < targetWidth; x++) {
                        const i = (y * targetWidth + x) * 4; // Index of the pixel in the RGBA data

                        // Get RGB values (alpha is ignored for grayscale conversion)
                        const r = data[i];
                        const g = data[i + 1];
                        const b = data[i + 2];

                        // Calculate luminance (grayscale value)
                        // This standard formula is used to convert RGB to grayscale
                        const grayscaleValue = (0.299 * r + 0.587 * g + 0.114 * b);

                        // If the luminance is below the threshold, the pixel is considered black (1)
                        // Otherwise, it's white (0)
                        bitmap[y * targetWidth + x] = grayscaleValue < 128 ? 1 : 0;
                    }
                }

                const commands = this.addImageToDocument({
                    data: bitmap,
                    height: targetHeight,
                    width: targetWidth
                });

                //Split into chunks to avoid stack overflow issues
                const chunks = chunk(commands, 5000);

                for (const chunk of chunks) {
                    this.escPosDoc.push(...chunk);
                }

                resolve();
            };

            img.onerror = reject; // Handle image loading errors
            img.src = imageUrl; // Set the image source to start loading
        });
    }

    /**
     * Generates ESC/POS commands for printing a 1-bit raster image using mode 0x21
     * (8-dot double density horizontal and double height).
     *
     * This mode prints 16 vertical dots for each pair of data bytes.
     * Each ESC * command prints an image strip 16 pixels high.
     *
     * @param {EscPosBitmapData} bitmapData - An object containing the image width, height, and the 1-bit bitmap data.
     *   - `width`: The width of the image in pixels (dots).
     *   - `height`: The height of the image in pixels (dots).
     *   - `data`: A flat array of numbers (0 or 1), where 1 represents a black pixel and 0 represents a white pixel.
     *             The data is ordered row by row, from top-left to bottom-right.
     */
    private addImageToDocument(bitmapData: EscPosBitmapData) {
        const { width, height, data } = bitmapData;

        const commands: number[] = [];

        //Set line spacing to 0 to avoid gaps between image chunks
        commands.push(
            0x1b, 0x33, 0x00
        );

        // Send image in chunks of width * 24 px
        for (let y = 0; y < height; y += 24) {

            commands.push(
                0x1B, // ESC
                0x2A, // *
                0x21, // 24-dot double-density
                width & 0xFF,          // nL: Low byte of totalBytesForHorizontalSlice
                (width >> 8) & 0xFF    // nH: High byte of totalBytesForHorizontalSlice
            );

            for (let x = 0; x < width; x++) {
                const bytes = [0, 0, 0];

                for (let k = 0; k < 3; k++) {
                    for (let yy = 0; yy < 8; yy++) {
                        const currentY = y + (k * 8) + yy;

                        // Break if we are exceeding image height
                        if (currentY > height) {
                            break;
                        }

                        // Check if pixel is set
                        if (data[(currentY * width) + x] === 1) {
                            bytes[k] |= (1 << (7 - yy));
                        }
                    }
                }

                commands.push(...bytes);
            }
        }

        return commands;
    }

    /**
     * Adds a pulse command to activate a cash drawer.
     * @param {EpsonDrawer} [drawer] - Drawer number (1 or 2).
     * @param {EpsonPulse} [time] - Pulse time.
     */
    public addPulse(drawer?: EpsonDrawer, time?: EpsonPulse): void {
        let drawerCode: number;
        let pulseCode: number;

        switch (drawer) {
            case EpsonDrawer.DRAWER_1:
                drawerCode = 0;
                break;
            case EpsonDrawer.DRAWER_2:
                drawerCode = 1;
                break;
            default:
                drawerCode = 0;
        }

        switch (time) {
            case EpsonPulse.PULSE_100:
                pulseCode = 50;
                break;
            case EpsonPulse.PULSE_200:
                pulseCode = 100;
                break;
            case EpsonPulse.PULSE_300:
                pulseCode = 150;
                break;
            case EpsonPulse.PULSE_400:
                pulseCode = 200;
                break;
            case EpsonPulse.PULSE_500:
                pulseCode = 250;
                break;
            default:
                pulseCode = 250;
        }

        this.escPosDoc.push(0x1b, 0x70, drawerCode, pulseCode, pulseCode);

        //Add RCH internal pulse
        this.escPosDoc.push(0x1b, 0x42, 0x01, 0x09);
    };

    /**
     * Sends the document to the printer.
     * @returns {Promise<NFSendResult>} A promise that resolves with the result of the print operation.
     */
    public async send(): Promise<NFSendResult> {
        return EscPosDriver.connectAndSend(this.networkSettings, this.escPosDoc).then(
            () => <NFSendResult>'PRINTED',
            (err: NFSendResult) => this.driverConfig.eReceiptMode ? 'PRINTED' : err
        );
    };

    /**
     * Attempts to connect to a printer.
     * @param {string} ip - IP address of the printer.
     * @param {number} port - Port number of the printer.
     * @returns {Promise<{ip: string, connected: boolean}>} A promise that resolves with the connection attempt result.
     */
    public static async connectAttempt(ip: string, port: number): Promise<{ ip: string, connected: boolean }> {
        if (ip === '0.0.0.0') {
            return { ip: ip, connected: true };
        }

        let connected = false;

        const tcpSocket = TilbyTcpSocketUtils.getSocket();

        if (tcpSocket) {
            try {
                await tcpSocket.connect(ip, port, { timeout: 1000 });
                connected = true;
            } catch (error) {
                //do nothing
            } finally {
                tcpSocket.close();
            }
        }

        return { ip: ip, connected: connected };
    };

    /**
    * Displays text on a specified printer's display.
    * @param {any} printer - Object containing printer connection info.
    * @param {string[]} textLines - Array of text lines to display.
    * @returns {Promise<void>} A promise that resolves when the text is sent successfully.
    */
    static async displayText(printer: any, textLines: string[]): Promise<void> {
        const docToSend = [0x1b, 0x3d, 0x02, 0x1b, 0x40, 0x0c, 0x0b];

        const printerInfo = {
            ipAddress: printer.ip_address,
            port: printer.port || 9100,
            connection: printer.connection_type
        };

        for (const line of textLines) {
            docToSend.push(...this.prototype.encodeText(line, printer.configuration?.codepage_mapping || 'epson'));
        }

        return this.connectAndSend(printerInfo, docToSend);
    };
}